import {useCallback, useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import ReactSelect, {components, OptionProps} from 'react-select';
import TableMetronic from '../../../app/common/components/table/TableMetronic';
import {ListService} from '../Types/service';
import {useIntl} from 'react-intl';
import {getStatusColor, getStatusLabel} from '../Helpers/stateHelper';
import {
  accessorService,
  accessorSite,
  getBuColLabel,
  getServiceColLabel,
  getSiteColLabel,
} from '../Helpers/ServiceHelper';
import EtatBadge from './EtatBadge';
import {isClientCIS} from '../../Auth/Utils/CustomerHelper';
import ActionIcon from '../../../Components/ActionIcon';
import {createSearchParams, useNavigate} from 'react-router-dom';
import {useGetMe} from '../../../app/hooks/useGetMe';
import styles from './Services.module.scss';

interface AccountOption {
  value: string;
  buId: number;
  buName: string;
  codeX3: string;
  count: number;
  label: string;
}

interface SiteOption {
  value: number;
  label: string;
  count: number;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const CheckboxOption = <T extends {label: string; count?: number}>(props: OptionProps<T, true>) => (
  <components.Option {...props}>
    <div className="d-flex align-items-start">
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        className="me-2 mt-1 flex-shrink-0"
      />
      <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
        {props.label}
      </label>
      {props.data.count != null && (
        <span className="ms-2 text-muted flex-shrink-0">({props.data.count})</span>
      )}
    </div>
  </components.Option>
);

interface MessagerieServicesProps {
  isLoading?: boolean;
  services?: ListService[];
}

const MessagerieServices = ({isLoading, services}: MessagerieServicesProps): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const customer = useGetMe();
  const [selectedAccounts, setSelectedAccounts] = useState<AccountOption[]>([]);
  const [selectedSites, setSelectedSites] = useState<SiteOption[]>([]);

  const accountOptions = useMemo<AccountOption[]>(() => {
    if (!services) return [];
    const map = new Map<string, AccountOption>();
    services.forEach((s) => {
      if (s.buId == null) return;
      const buName = s.buName ?? '';
      const codeX3 = s.codeX3 ?? '';
      const key = `${s.buId}|${codeX3}|${buName}`;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = [s.buId, codeX3, buName].filter((p) => p !== '' && p != null).join(' - ');
      map.set(key, {value: key, buId: s.buId, buName, codeX3, label, count: 1});
    });
    return Array.from(map.values()).sort(
      (a, b) =>
        a.buId - b.buId || a.codeX3.localeCompare(b.codeX3) || a.buName.localeCompare(b.buName)
    );
  }, [services]);

  const siteOptions = useMemo<SiteOption[]>(() => {
    if (!services) return [];
    const map = new Map<number, SiteOption>();
    services.forEach((s) => {
      const site = s.site;
      if (site?.id == null) return;
      const existing = map.get(site.id);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = `${site.id} - ${[site.address, site.zipCode, site.city]
        .filter(Boolean)
        .join(' ')}`.trim();
      map.set(site.id, {value: site.id, label, count: 1});
    });
    return Array.from(map.values()).sort((a, b) => a.value - b.value);
  }, [services]);

  const filteredServices = useMemo(() => {
    if (!services) return services;
    if (selectedAccounts.length === 0 && selectedSites.length === 0) return services;
    const accountKeys = new Set(selectedAccounts.map((o) => o.value));
    const siteIds = new Set(selectedSites.map((o) => o.value));
    return services.filter((s) => {
      const accountOk =
        accountKeys.size === 0 ||
        (s.buId != null && accountKeys.has(`${s.buId}|${s.codeX3 ?? ''}|${s.buName ?? ''}`));
      const siteOk = siteIds.size === 0 || (s.site?.id != null && siteIds.has(s.site.id));
      return accountOk && siteOk;
    });
  }, [services, selectedAccounts, selectedSites]);
  const goToEmails = useCallback(
    (row: ListService) => {
      const searchParams = new URLSearchParams();
      searchParams.append('buId', row.buId.toString());
      searchParams.append('link_name', row.link_name.toString());
      searchParams.append('serviceId', row.serviceId.toString());
      navigate({
        pathname: `/services/domain/emails`,
        search: createSearchParams(searchParams).toString(),
      });
    },
    [navigate]
  );

  const renderMessagerieAction = useCallback(
    (row: ListService): JSX.Element | null => {
      if (!isClientCIS(customer)) return null;
      return (
        <ActionIcon
          label="Détails"
          faIcon="fs-1 fa-solid fa-envelope"
          onClick={() => goToEmails(row)}
        />
      );
    },
    [customer, goToEmails]
  );

  const renderMessagerieActionButton = useCallback(
    (row: ListService): JSX.Element | null => {
      if (!isClientCIS(customer)) return null;
      return (
        <button
          type="button"
          className="btn btn-sm btn-light-primary"
          onClick={() => goToEmails(row)}
        >
          <i className="fa-solid fa-envelope me-2" />
          Détails
        </button>
      );
    },
    [customer, goToEmails]
  );

  const columns = useMemo<ColumnDef<ListService>[]>(
    () => [
      {
        header: 'Compte',
        accessorKey: 'buId',
        cell: getBuColLabel,
        size: 130,
      },
      {
        header: 'Site',
        accessorKey: 'siteId',
        cell: getSiteColLabel,
        accessorFn: accessorSite,
        size: 180,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.site?.id ?? 0;
          const idB = rowB.original.site?.id ?? 0;
          return idA - idB;
        },
      },
      {
        header: 'Etat',
        accessorKey: 'state',
        size: 130,
        cell: (info) => {
          const status = info.row.original.state;
          const color = getStatusColor(status);
          return status ? <EtatBadge color={color} label={getStatusLabel(status, intl)} /> : '-';
        },
      },
      {
        header: 'Service',
        accessorKey: 'service',
        cell: getServiceColLabel,
        accessorFn: accessorService,
        size: 220,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.serviceId ?? 0;
          const idB = rowB.original.serviceId ?? 0;
          return idA - idB;
        },
      },
      {
        header: 'Actions',
        accessorKey: 'domaine',
        size: 60,
        cell: (info) => renderMessagerieAction(info.row.original),
      },
    ],
    [intl, renderMessagerieAction]
  );

  const filtersToolbar = (
    <>
      <div style={{minWidth: 220, flex: 1, maxWidth: 320}}>
        <ReactSelect<AccountOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={accountOptions}
          value={selectedAccounts}
          onChange={(vals) => setSelectedAccounts(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_ACCOUNTS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div style={{minWidth: 300, flex: 1.5, maxWidth: 450}}>
        <ReactSelect<SiteOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={siteOptions}
          value={selectedSites}
          onChange={(vals) => setSelectedSites(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_SITES'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
    </>
  );
  const renderMobileCard = (row: ListService): JSX.Element => {
    const status = row.state;
    const cellInfo = {row: {original: row}};

    return (
      <div className={styles.mobileCardBody}>
        <div className={styles.mobileCardHeader}>
          <span style={{minWidth: 0, wordBreak: 'break-word'}}>{getServiceColLabel(cellInfo)}</span>
          {status && (
            <span className="flex-shrink-0">
              <EtatBadge color={getStatusColor(status)} label={getStatusLabel(status, intl)} />
            </span>
          )}
        </div>
        <div>
          <span className={styles.mobileCardSectionLabel}>Compte</span>
          {getBuColLabel(cellInfo)}
        </div>
        <div>
          <span className={styles.mobileCardSectionLabel}>Site</span>
          {getSiteColLabel(cellInfo)}
        </div>
        {isClientCIS(customer) && <div className="mt-1">{renderMessagerieActionButton(row)}</div>}
      </div>
    );
  };

  return (
    <TableMetronic<ListService>
      toolbar={filtersToolbar}
      stackedHeader
      title={intl.formatMessage({id: 'MENU.CLOUD.ZIMBRA'})}
      data={filteredServices ?? []}
      columns={columns}
      renderMobileCard={renderMobileCard}
      isLoading={isLoading}
      defaultSorting={[{id: 'siteId', desc: true}]}
      searchBar
      pagination
    />
  );
};

export default MessagerieServices;
